import type { z } from "zod";

type PaymentPlanShape = {
  mode: "upfront" | "interest_free" | "interest_bearing";
  totalAmount: number;
  upfrontAmount: number;
  upfrontDate?: string;
  installmentCount: number;
  firstInstallmentDueDate?: string;
  confirmations: Array<{ kind: "upfront" } | { kind: "installment"; installmentNumber: number }>;
};

export const validatePaymentPlanShape = (plan: PaymentPlanShape, context: z.RefinementCtx) => {
  if (plan.upfrontAmount > plan.totalAmount) {
    addIssue(context, "case_payment_validation_upfront_amount", ["upfrontAmount"]);
  }
  if (plan.upfrontAmount > 0 && !plan.upfrontDate) {
    addIssue(context, "case_payment_validation_upfront_date_required", ["upfrontDate"]);
  }
  if (plan.upfrontAmount === 0 && plan.upfrontDate) {
    addIssue(context, "case_payment_validation_upfront_date_without_amount", ["upfrontDate"]);
  }
  if (plan.mode === "upfront") validateUpfrontMode(plan, context);
  else validateInstallmentMode(plan, context);
  validateConfirmationReferences(plan, context);
};

const validateUpfrontMode = (plan: PaymentPlanShape, context: z.RefinementCtx) => {
  if (plan.upfrontAmount !== plan.totalAmount) {
    addIssue(context, "case_payment_validation_upfront_total", ["upfrontAmount"]);
  }
  if (!plan.upfrontDate) {
    addIssue(context, "case_payment_validation_upfront_date_required", ["upfrontDate"]);
  }
  if (plan.installmentCount !== 0) {
    addIssue(context, "case_payment_validation_upfront_installments", ["installmentCount"]);
  }
  if (plan.firstInstallmentDueDate) {
    addIssue(context, "case_payment_validation_upfront_first_due_date", [
      "firstInstallmentDueDate",
    ]);
  }
};

const validateInstallmentMode = (plan: PaymentPlanShape, context: z.RefinementCtx) => {
  if (plan.upfrontAmount >= plan.totalAmount) {
    addIssue(context, "case_payment_validation_installment_balance", ["upfrontAmount"]);
  }
  if (plan.installmentCount < 1) {
    addIssue(context, "case_payment_validation_installment_count", ["installmentCount"]);
  }
  if (!plan.firstInstallmentDueDate) {
    addIssue(context, "case_payment_validation_first_due_date_required", [
      "firstInstallmentDueDate",
    ]);
  }
};

const validateConfirmationReferences = (plan: PaymentPlanShape, context: z.RefinementCtx) => {
  const keys = new Set<string>();
  plan.confirmations.forEach((confirmation, index) => {
    const key =
      confirmation.kind === "upfront" ? "upfront" : `installment:${confirmation.installmentNumber}`;
    if (keys.has(key)) {
      addIssue(context, "case_payment_validation_duplicate_confirmation", ["confirmations", index]);
    }
    keys.add(key);
    if (confirmation.kind === "upfront" && plan.upfrontAmount <= 0) {
      addIssue(context, "case_payment_validation_confirmation_reference", ["confirmations", index]);
    }
    if (
      confirmation.kind === "installment" &&
      confirmation.installmentNumber > plan.installmentCount
    ) {
      addIssue(context, "case_payment_validation_confirmation_reference", ["confirmations", index]);
    }
  });
};

const addIssue = (context: z.RefinementCtx, message: string, path: PropertyKey[]) => {
  context.addIssue({ code: "custom", message, path });
};
